import { AllUnitEnum } from '../../models/convertUnitOfMeasureEnum'
import {
  FluidApplication,
  FluidCapacity,
  FluidCapacityItem,
  FluidItem
} from '../../sdk'
import {
  convertQuantityToDifferentUnit,
  MOTOR_UNIT_TO_ENUM
} from '../../utils/convertFluidsUnits/convertFluidsUnits'
import { roundToOneDecimal } from '../../utils/roundToTwoDecimal/roundToTwoDecimal'
export const DUMMY_ATTRIBUTE_KEY = 'dummyKey'
function getAttributeKey(
  attributeDescriptors: { [key: string]: string } | undefined
) {
  // only support engine configuration id for now
  if (attributeDescriptors && Object.keys(attributeDescriptors).length > 0) {
    return Object.values(attributeDescriptors).slice().sort().join('-')
  }
  return DUMMY_ATTRIBUTE_KEY
}
export function convertKeyValueToString(
  object: { [key: string]: string | number } | undefined
) {
  // CO is removed, CO represents country
  return Object.entries(object || {})
    .filter(([key]) => key !== 'CO')
    .map(([key, value]) => key + ': ' + value)
    .join(' ')
}
interface ModifiedFluidCapacity extends FluidCapacity {
  groupedItems: FluidCapacityItem[][]
}
interface ModifiedFluidItem {
  fluidItem: FluidItem
  capacities: { [key: string]: ModifiedFluidCapacity }
}
export interface AttributesItem {
  applicationId: number
  attributes: { [key: string]: string } | undefined
  fluidItems: { [key: string]: ModifiedFluidItem }
}
export function getFluidApplicationTree(applications: FluidApplication[]) {
  const result: { [key: string]: AttributesItem } = {}
  applications.forEach((application) => {
    if (!application.items) {
      return
    }
    const attributeKey = getAttributeKey(application.attributeDescriptors)

    // get a map of {capacityUUID: ModifiedFluidCapacity}
    // filter out units not in enum map
    // convert unit to InventoryPartInstanceInfoUnitOfMeasureEnum if there is matched unit
    // use quart as unit to present the capacity if there is
    const capacities: { [key: string]: ModifiedFluidCapacity } = {}
    ;(application.capacities || []).forEach((fluidCapacity: FluidCapacity) => {
      const quartItems: { [key: number]: FluidCapacityItem[] } = {}
      const sortedItems = (fluidCapacity.items || [])
        .slice()
        .filter((i) => !!i.unitOfMeasure)
        .filter((i) => !!i.value)
        .filter((i) => i.unitOfMeasure! in MOTOR_UNIT_TO_ENUM)
        .map((item) => ({
          ...item,
          unitOfMeasure: MOTOR_UNIT_TO_ENUM[item.unitOfMeasure!]
        }))
        .sort(
          (a, b) =>
            (a.unitOfMeasure === 'quart' ? -1 : 1) -
            (b.unitOfMeasure === 'quart' ? -1 : 1)
        )
      for (const item of sortedItems) {
        const qtyInQuart = convertQuantityToDifferentUnit(
          item.value!,
          item.unitOfMeasure! as AllUnitEnum,
          'quart'
        )
        const foundQuartKey = Object.keys(quartItems).find(
          (key) => roundToOneDecimal(Math.abs(qtyInQuart - Number(key))) <= 0.1
        )
        if (foundQuartKey) {
          quartItems[Number(foundQuartKey)].push(item)
        } else {
          quartItems[qtyInQuart] = [item]
        }
      }

      capacities[fluidCapacity.id!] = {
        ...fluidCapacity,
        groupedItems: Object.values(quartItems)
      }
    })

    // if attributeKey does not exist, create a key
    !(attributeKey in result) &&
      (result[attributeKey] = {
        attributes: application.attributeDescriptors,
        fluidItems: {},
        applicationId: application.id!
      })
    const existingApp = result[attributeKey]
    // loop through each fluid item
    application.items.forEach((fluidItem) => {
      // if item key does not exsit, create one and assign the fluidItem as value
      const itemKey = fluidItem.id!
      !(itemKey in existingApp.fluidItems) &&
        (existingApp.fluidItems[itemKey] = {
          fluidItem,
          capacities: {}
        })
      const exItem = existingApp.fluidItems[itemKey]
      // if the capacity key does not exist under this item, create a new one and assign the capacity item as value
      Object.keys(capacities).forEach((cId) => {
        !(cId in exItem.capacities) &&
          (exItem.capacities[cId] = capacities[cId])
      })
    })
  })
  return result
}
